import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewEncapsulation
} from '@angular/core';
import { FormGroup, FormsModule, NgForm } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ErrorsService } from '@partners/errors';
import { isFunction, isPromise, takeOnce, TAsyncFnWrapper } from '@partners/utils';
import { delay, finalize, isObservable, Observable } from 'rxjs';
import { IFormConfig, IFormFactory, IFormField } from '../../model';

@Component({
  standalone: true,
  selector: 'feature-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, FormsModule],
  template: `
    <ng-container *ngIf="formConfig && formGroup">
      <ng-content></ng-content>
    </ng-container>
  `,
  host: {
    class: 'feature-form'
  },
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true
      }
    }
  ]
})
export class FeatureFormComponent implements OnDestroy {
  @Input()
  set formFactory(value: IFormFactory) {
    const { name, form, config } = value;
    this.formName = name || config.name;
    this.formGroup = form;
    this.formConfig = config;
  }

  /** the form's own pending indicator — consumers can read this via viewChild instead of managing their own signal */
  readonly isPending = signal<boolean>(false);

  sendFn = input<TAsyncFnWrapper>(undefined);
  /** delay (ms) before the pending state is cleared once the async submit completes (mirrors filled-button's clickFnTrailTime) */
  trailTime = input<number>(0);

  @Output()
  send: EventEmitter<any>;

  get formValue(): any {
    return this.formGroup.getRawValue();
  }

  get locals(): IFormFactory {
    return { form: this.formGroup, config: this.formConfig };
  }

  submitted$$ = signal<boolean>(false);

  @ContentChild('formHandler')
  formHandler!: NgForm;

  formName: string;
  formGroup: FormGroup;
  formConfig: IFormConfig;

  private _changeDetector = inject(ChangeDetectorRef);
  private _clientErrors = inject(ErrorsService);

  constructor() {
    this.send = new EventEmitter<any>();
  }

  ngOnDestroy(): void {
    this.send.complete();
  }

  ngAfterViewInit(): void {
    this.overrideSubmit();
  }

  findFieldConfig(fieldName: string): IFormField {
    return this.formConfig.find(fieldName);
  }

  private overrideSubmit() {
    const theForm = this.formHandler;

    if (!theForm) {
      this._clientErrors.throwLocalError({ message: 'Form handler not found' });
    }

    theForm.onSubmit = (event: any): boolean => {
      this.submitted$$.set(true);

      event.stopPropagation();
      event.preventDefault();

      /**
       * guard against re-submits while an async submit (sendFn) is still in flight.
       * the button's [disabled] binding only applies on the next change-detection pass,
       * so a fast double-click could otherwise dispatch a second native submit before then.
       */
      if (this.isPending()) {
        return false;
      }

      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      this._changeDetector.detectChanges();

      const { valid } = this.formGroup;

      if (!valid) {
        return false;
      }

      const value = this.formValue;

      if (isFunction(this.sendFn())) {
        const fnResult = this.sendFn()();

        /** the form is responsible for setting its own isPending state before and after the async call (+ on error) */
        this.isPending.set(true);

        if (isObservable(fnResult)) {
          (fnResult as Observable<unknown>).pipe(
            takeOnce(),
            delay(this.trailTime()),
            finalize(() => this.isPending.set(false))
          ).subscribe();
        } else if (isPromise(fnResult)) {
          (fnResult as Promise<unknown>).finally(() => this.isPending.set(false));
        }
      } else {
        this.send.emit(value);
      }

      this._changeDetector.detectChanges();

      return false;
    };
  }
}
